/**
 * Whisper Client - Audio transcription via faster-whisper
 * Routed through backend gateway: /api/whisper/*
 */

import { getApiEndpoints } from '../config';

const getApiUrl = (path: string) => {
  return `${getApiEndpoints().whisper}${path}`;
};

// Auth: user JWT token (gateway validates and forwards to whisper)
const getAuthHeaders = (): HeadersInit => {
  try {
    const authData = localStorage.getItem('auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      const token = parsed.accessToken || parsed.token;
      if (token) return { Authorization: `Bearer ${token}` };
    }
  } catch { /* ignore */ }
  return {};
};

export interface WhisperTranscriptionResponse {
  text: string;
  language?: string;
  duration?: number;
  segments?: {
    id: number;
    start: number;
    end: number;
    text: string;
  }[];
}

export interface WhisperDiarizedSegment {
  start: number;
  end: number;
  text: string;
  speaker: string;
}

export interface WhisperDiarizedResponse {
  text: string;
  language?: string;
  duration?: number;
  speakers: string[];
  speaker_count: number;
  segments: WhisperDiarizedSegment[];
}

export interface DiarizeOptions {
  language?: string;
  numSpeakers?: number;
  minSpeakers?: number;
  maxSpeakers?: number;
}

export interface WhisperHealthResponse {
  status: string;
  model: string;
  diarization_available?: boolean;
}

/**
 * Verifica che Whisper sia raggiungibile e quali funzionalita' sono disponibili
 */
export async function checkWhisperHealth(): Promise<WhisperHealthResponse | null> {
  try {
    const res = await fetch(getApiUrl("/health"));
    if (res.ok) {
      return res.json();
    }
    // Se non c'e' health, prova la root
    const res2 = await fetch(getApiUrl("/"));
    if (res2.ok || res2.status === 404) {
      return { status: "ok", model: "unknown" };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Verifica se la diarizzazione e' disponibile
 */
export async function isDiarizationAvailable(): Promise<boolean> {
  const health = await checkWhisperHealth();
  return health?.diarization_available ?? false;
}

/**
 * Trascrive un file audio
 */
export async function transcribe(
  audioFile: File | Blob,
  language?: string
): Promise<WhisperTranscriptionResponse> {
  const formData = new FormData();

  // Aggiungi il file audio
  if (audioFile instanceof File) {
    formData.append("file", audioFile);
  } else {
    formData.append("file", audioFile, "audio.webm");
  }

  // Aggiungi la lingua se specificata
  if (language) {
    formData.append("language", language);
  }

  // Task (transcribe o translate)
  formData.append("task", "transcribe");

  const res = await fetch(getApiUrl("/transcribe"), {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Whisper transcription error: ${res.status} ${res.statusText} - ${errorText}`);
  }

  return res.json();
}

/**
 * Trascrive audio da un Blob registrato dal browser
 */
export async function transcribeBlob(
  blob: Blob,
  language?: string
): Promise<string> {
  const response = await transcribe(blob, language);
  return response.text;
}

/**
 * Trascrive audio con identificazione degli speaker (diarization)
 *
 * Richiede che pyannote-audio sia installato sul server.
 * Gli speaker sono identificati come SPEAKER_00, SPEAKER_01, etc.
 */
export async function transcribeWithDiarization(
  audioFile: File | Blob,
  options?: DiarizeOptions
): Promise<WhisperDiarizedResponse> {
  const formData = new FormData();

  // Aggiungi il file audio
  if (audioFile instanceof File) {
    formData.append("file", audioFile);
  } else {
    formData.append("file", audioFile, "audio.webm");
  }

  // Aggiungi le opzioni
  if (options?.language) {
    formData.append("language", options.language);
  }
  if (options?.numSpeakers !== undefined) {
    formData.append("num_speakers", options.numSpeakers.toString());
  }
  if (options?.minSpeakers !== undefined) {
    formData.append("min_speakers", options.minSpeakers.toString());
  }
  if (options?.maxSpeakers !== undefined) {
    formData.append("max_speakers", options.maxSpeakers.toString());
  }

  formData.append("task", "transcribe");

  const res = await fetch(getApiUrl("/transcribe-diarize"), {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Whisper diarization error: ${res.status} ${res.statusText} - ${errorText}`);
  }

  return res.json();
}

/**
 * Trascrive audio con diarization da un Blob
 * Restituisce il testo formattato con i nomi degli speaker
 */
export async function transcribeBlobWithSpeakers(
  blob: Blob,
  options?: DiarizeOptions
): Promise<{ text: string; speakers: string[]; segments: WhisperDiarizedSegment[] }> {
  const response = await transcribeWithDiarization(blob, options);
  return {
    text: response.text,
    speakers: response.speakers,
    segments: response.segments,
  };
}

/**
 * Registra audio dal microfono e restituisce un Blob
 */
export function createAudioRecorder(): {
  start: () => Promise<void>;
  stop: () => Promise<Blob>;
  isRecording: () => boolean;
} {
  let mediaRecorder: MediaRecorder | null = null;
  let audioChunks: Blob[] = [];
  let recording = false;

  return {
    async start() {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunks = [];

      // Usa webm se disponibile, altrimenti mp4
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "audio/ogg";

      mediaRecorder = new MediaRecorder(stream, { mimeType });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.start(100); // Chunk ogni 100ms
      recording = true;
    },

    async stop(): Promise<Blob> {
      return new Promise((resolve, reject) => {
        if (!mediaRecorder) {
          reject(new Error("Recorder not started"));
          return;
        }

        mediaRecorder.onstop = () => {
          const blob = new Blob(audioChunks, { type: mediaRecorder!.mimeType });
          // Ferma le tracce audio
          mediaRecorder!.stream.getTracks().forEach((track) => track.stop());
          recording = false;
          resolve(blob);
        };

        mediaRecorder.stop();
      });
    },

    isRecording() {
      return recording;
    },
  };
}

export const whisperClient = {
  checkHealth: checkWhisperHealth,
  isDiarizationAvailable,
  transcribe,
  transcribeBlob,
  transcribeWithDiarization,
  transcribeBlobWithSpeakers,
  createAudioRecorder,
};

export default whisperClient;
